<script setup>
import { ref, computed, onMounted, watch } from "vue";
import Hero from "@app/Hero.vue";

import SortResultsBy from "@components/ResultSort.vue";
import ResultCard from "@components/ResultCard.vue";
import LearnMore from "@components/LearnMore.vue";
import Filter from "@components/Filter.vue";
import TJButton from "@components/TJButton.vue";
import FieldCheckbox from "@components/FieldCheckbox.vue";
import FieldSearch from "@components/FieldSearch.vue";
import FieldSelect from "@components/FieldSelect.vue";
import Breadcrumb from "@components/Breadcrumb.vue";
import DestinationInfo from "@components/DestinationInfo.vue";
import ModalConsultationInfo from "@components/ModalConsultationInfo.vue";
import Spinner from "@components/Spinner.vue";

import { useBookingStore } from "@stores/BookingStore";
import { useFilterStore } from "@stores/FilterStore";

import Map from "@components/ModalMap.vue";
import Slider from "primevue/slider";
import Dialog from "primevue/dialog";
import Paginator from "primevue/paginator";
import languages from "@data/languages.js";
import { cloneDeep, filter } from "lodash-es";

const bookingStore = useBookingStore();
const filterStore = useFilterStore();

const props = defineProps({});

const showFilter = ref(true);
const sortBy = ref("distance"); // Default sort by distance
const infoModalVisible = ref(false);
const isLoading = ref(false);

// Pagination-specific state
const itemsPerPage = ref(10); // Corresponds to Paginator's 'rows'
const currentPage = ref(0); // Corresponds to Paginator's 'first' (page 0 is first item)

const initFilter = () => {
  const isMobile = window.innerWidth < 1280;
  showFilter.value = !isMobile;
};

const toggleFilter = () => {
  showFilter.value = !showFilter.value;
};

const mapModalVisible = ref(false);
const results = ref([]);

const getClinics = async () => {
  isLoading.value = true;

  try {
    const response = await bookingStore.getClinics(
      bookingStore.query?.location,
      bookingStore.query?.vaccinations
    );

    if (response.status === 200) {
      // const originalData = cloneDeep(response.data);

      // let testData = [];
      // while (testData.length < 20) {
      //   // Concatenate deep copies of the original data to ensure unique references
      //   testData = testData.concat(originalData.map((item) => cloneDeep(item)));
      // }

      // // Trim down to exactly 20 items if necessary
      // testData = testData.slice(0, 20);

      // Store the generated test data in results.value
      // results.value = testData;

      // console.log("Clinics fetched successfully:", response.data);
      results.value = response.data;
      filterStore.setResults(response.data);
      bookingStore.setResults(response.data);
    } else {
      console.error("Failed to fetch clinics:", response.data);
    }
  } catch (error) {
    console.error("Failed to fetch clinics:", error);
  }

  isLoading.value = false;
};

// Computed property to calculate the paginated results
const paginatedResults = computed(() => {
  const start = currentPage.value * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filterStore.filteredResults.slice(start, end);
});

// NEW: Method to handle PrimeVue Paginator's page change event
const onPageChange = (event) => {
  currentPage.value = event.page; // PrimeVue event.page is 0-indexed
  itemsPerPage.value = event.rows; // PrimeVue event.rows gives current items per page
};

//watch for changes in boookingStore.serviceType
watch(
  () => bookingStore.serviceType,
  (newServiceType, oldServiceType) => {
    //if the service type changes, i.e old value is not equal to new value
    if (newServiceType !== oldServiceType) {
      // Reset the results and pagination
      results.value = [];
      currentPage.value = 0; // Reset to first page
      itemsPerPage.value = 10; // Reset to default items per page
      bookingStore.resetClinics(); // Reset clinics in booking store
    }
  }
);

onMounted(() => {
  initFilter();
  getClinics();
});
</script>

<template>
  <!-- <pre>
  {{ bookingStore.query.patients }}
  </pre> -->
  <Hero :style="'simple'" />

  <section class="flex flex-col pt-30 pb-60 container">
    <Breadcrumb />

    <div class="mb-60" v-if="bookingStore.serviceType === 'consultations'">
      <DestinationInfo />
    </div>

    <section class="flex max-xl:flex-col gap-12">
      <div class="w-full md:max-w-[300px] xl:max-w-[230px]">
        <LearnMore />

        <div
          v-if="results.length"
          class="hidden xl:block bg-white mb-60 border border-grey-border rounded-2xl overflow-hidden">
          <img
            :src="require(`@images/map.jpg`)"
            class="w-full h-[200px] object-cover"
            alt="map" />
          <button
            class="block p-10 w-full text-blue text-center"
            @click="mapModalVisible = true">
            View in Map
          </button>
          <Dialog
            v-model:visible="mapModalVisible"
            modal
            header="Within 5 miles"
            class="sm:m-20 rounded-2xl w-full h-full overflow-hidden">
            <Map :clinics="results" />
          </Dialog>
        </div>

        <div v-if="results.length" class="xl:hidden flex justify-between">
          <button
            @click="toggleFilter"
            class="border border-blue min-w-[140px] text-blue btn">
            Sort & Filter
          </button>

          <button
            class="border border-blue min-w-[140px] text-blue btn"
            @click="mapModalVisible = true">
            View in Map
          </button>
        </div>

        <Filter v-if="showFilter && results.length" />
      </div>

      <section class="w-full">
        <!-- Always render ResultSort (hidden) so it can trigger the filter after 2 seconds -->
        <div style="display: none;">
          <SortResultsBy labelPosition="inline" autoApply />
        </div>

        <Spinner v-if="isLoading || (!isLoading && !filterStore.isInitialFilterApplied)" />

        <header
          v-if="!isLoading && filterStore.isInitialFilterApplied && filterStore.filteredResults.length"
          class="flex justify-between mb-30">
          <h1 class="font-medium text-20 text-grey">
            Search Results: {{ filterStore.filteredResults.length }} within 5 miles
          </h1>

          <div class="hidden lg:flex">
            <SortResultsBy labelPosition="inline" autoApply />
          </div>
        </header>

        <div v-if="!isLoading && filterStore.isInitialFilterApplied && !filterStore.filteredResults.length">
          <p class="font-medium text-18 text-grey text-center">
            No clinics found for your search criteria.
          </p>
        </div>

        <div
          v-if="bookingStore.serviceType === 'consultations' && filterStore.isInitialFilterApplied && filterStore.filteredResults.length"
          class="flex justify-between gap-12 bg-blue-bg mb-20 p-20 border border-blue rounded-xl">
          <p>
            Listed below are nearby clinics that offer non-refundable travel
            consultation services
          </p>
          <p @click="infoModalVisible = true" class="text-blue cursor-pointer">
            What is a travel consultation?
          </p>
        </div>

        <Dialog
          v-model:visible="infoModalVisible"
          header="What is travel consultation?"
          class="max-w-[720px] max-h-[650px]">
          <ModalConsultationInfo />
        </Dialog>

        <ul v-if="!isLoading && filterStore.isInitialFilterApplied && filterStore.filteredResults.length">
          <li
            v-for="(item, index) in paginatedResults"
            :key="index"
            class="mb-30">
            <ResultCard :clinic="item" />
          </li>
        </ul>

        <footer
          v-if="!isLoading && filterStore.isInitialFilterApplied && filterStore.filteredResults.length"
          class="flex justify-center items-center mt-40">
          <Paginator
            :rows="itemsPerPage"
            :totalRecords="filterStore.filteredResults.length"
            :rowsPerPageOptions="[10, 20, 30]"
            @page="onPageChange"></Paginator>
        </footer>
      </section>
    </section>
  </section>
</template>

<style scoped lang="scss"></style>
